import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import { ToastError } from "../../../../UiComponents/Toaster/Toast";
import { requiresSeparateAadhar, isNational, hasCompanyLeg } from "../utils/companyRules";
import { isValidAadharNumber } from "../utils/aadharOcr";
import { PRIMARY_BUTTON_SX, SECONDARY_BUTTON_SX, DANGER_BUTTON_SX } from "./formStyles";
import ConfirmBox from "../../../../UiComponents/ConfirmBox/ConfirmBox";

/**
 * Form Navigation Buttons Component
 * Handles Back, Cancel, Next, and Submit buttons for the form stepper
 */
const FormNavigationButtons = ({
  activeStep,
  handleBack,
  handleReset,
  handleNext,
  handleNextStep,
  saveCustomerDetails,
  values,
  validateForm,
  setTouched,
  touched,
  isEditMode,
  showCalculationResult,
  // Edit mode on a policy with parts: what the Update button does, e.g.
  // "Update & re-submit 2 parts (OD, PA)". Null keeps plain "Update".
  updateLabel = null,
}) => {
  // Cancel asks first (the owner, 2026-10-27): one click used to throw the whole form away
  const [confirmCancel, setConfirmCancel] = useState(false);
  const handleNextClick = async () => {
    if (activeStep === 0) {
      // Validate required fields for Step 0
      const errors = await validateForm();
      const step0Fields = [
        "company",
        // Company routing — hidden fields derived from the Make & Model master
        // and the PA Cover Company dropdown. Their rules never gated Next
        // before, so a bad routing only surfaced at the server.
        "odCompany",
        "tpCompany",
        "paCoverCompany",
        "makeModel",
        "makeModelCombo",
        "rtoCityLocation",
        "zone",
        "idv",
        "manufacturingYear",
        "manufacturingMonth",
        "purchaseDate",
        "registrationDate",
        "paCover",
        "paCoverAmount",
        "notPaCoverReason",
      ];
      const hasStep0Errors = step0Fields.some((field) => errors[field]);

      if (hasStep0Errors) {
        // Mark all step 0 fields as touched to show inline errors
        const touchedFields = {};
        step0Fields.forEach((field) => {
          touchedFields[field] = true;
        });
        setTouched({ ...touched, ...touchedFields });

        if (errors.paCover) {
          ToastError(errors.paCover);
        }
        if (errors.paCoverAmount) {
          ToastError(errors.paCoverAmount);
        }
        if (errors.notPaCoverReason) {
          ToastError(errors.notPaCoverReason);
        }
        // Company field is hidden in the UI — surface its error as a toast
        if (errors.company) {
          ToastError(
            "Company not set — select a Make & Model that has a company mapped"
          );
        }
        return;
      }

      // Check nominee fields — PA Cover requires them for any insurer;
      // National requires them regardless of PA Cover, since its portal
      // demands a nominee on every quote (see validationSchemas.js). Without
      // this OR, a National policy with PA Cover off sailed past Step 0 with
      // an empty, but still Yup-required, nominee.
      if (values.paCover || hasCompanyLeg(values, isNational)) {
        const paFields = [
          "nomineeName",
          "nomineeGender",
          "nomineeRelation",
          "nomineeType",
          "nomineeShare",
          "nomineeDob",
          "nomineeAge",
          "appointeeName",
          "otherRelationName",
          "paCoverYears",
        ];
        const hasPAErrors = paFields.some((field) => errors[field]);
        if (hasPAErrors) {
          const touchedFields = {};
          paFields.forEach((field) => {
            touchedFields[field] = true;
          });
          setTouched({ ...touched, ...touchedFields });
          return;
        }
      }

      // If not calculated yet, calculate and stay on Step 0
      if (!showCalculationResult) {
        await handleNextStep(values);
        return;
      }

      // Already calculated → advance to next step
      handleNext();
      return;
    }

    if (activeStep === 1) {
      // Validate required fields for Step 1 (Customer Details)
      const errors = await validateForm();
      const step1Fields = [
        // Customer Details
        "fullName",
        "surname",
        "dateOfBirth",
        "gender",
        "proposerTitle",
        "fatherName",

        // Address Details
        "flatDoorNo",
        "floorNo",
        "buildingName",
        "blockName",
        "roadStreetLane",
        "areaAndLocality",
        "state",
        "country",
        "pincode",
        "email",
        "mobileNumber",

        // Vehicle Details
        "engineNumber",
        "chassisNumber",

        // The customer's existing TP policy (OD only: required; two insurers: checked when typed)
        "existingTpInsurer",
        "existingTpPolicyNumber",
        "existingTpStartDate",
        "existingTpEndDate",
      ];

      const firstBadField = step1Fields.find((field) => errors[field]);

      if (firstBadField) {
        // Mark all step 1 fields as touched to show inline errors
        const touchedFields = {};
        step1Fields.forEach((field) => {
          touchedFields[field] = true;
        });
        setTouched({ ...touched, ...touchedFields });
        // Name the field. The inline messages are the real feedback, but this
        // step is long enough that a missing Floor No sits well below the
        // fold — returning silently reads as a dead Next button.
        ToastError(
          errors[firstBadField] || "Please fill all the required fields"
        );
        return;
      }

      // Check Financier details if applicable
      if (values.hasFinancier) {
        if (errors.financierName) {
          setTouched({ ...touched, financierName: true });
          return;
        }
      }

      // Aadhaar card upload is mandatory for new policies
      // (in edit mode the previously uploaded file is kept if not re-uploaded)
      // Reliance: BOTH sides must be uploaded separately — a single merged
      // upload is not accepted
      const needsSeparateAadhar = hasCompanyLeg(values, requiresSeparateAadhar);
      if (!isEditMode && needsSeparateAadhar) {
        if (!values.aadharCardFront || !values.aadharCardBack) {
          ToastError(
            `For ${values.company}, BOTH Aadhaar card front and back sides are mandatory`
          );
          return;
        }
      } else if (!isEditMode && !values.aadharCard) {
        if (values.aadharCardFront || values.aadharCardBack) {
          ToastError("Please upload BOTH front and back of the Aadhaar card");
        } else {
          ToastError("Aadhaar card is mandatory. Please upload it to continue");
        }
        return;
      }

      // The Aadhaar number is produced by OCR and has no input field, so a bad
      // read has no manual fallback — catch it here or it reaches the insurer
      // blank. Skipped in edit mode: policies saved before this field existed
      // have no stored number and must stay editable.
      if (!isEditMode && !isValidAadharNumber(values.aadharNumber)) {
        ToastError(
          "Aadhaar card number is not clear. Please upload a clearer image of the Aadhaar card."
        );
        return;
      }

      handleNext();
      return;
    }

    // Other steps just advance
    handleNext();
  };

  return (
    <>
      {/* <Grid item xs={12}>
        <Divider
          sx={{
            backgroundColor: "#EAECF0",
            borderWidth: 1,
            mt: 3,
            mb: 2,
          }}
        />
      </Grid> */}

      {/* The step's buttons in a bar that stays at the bottom of the window while the step scrolls (2026-10-23;
          .op-action-bar in onlinePolicyForm.scss). Cancel on the left, away from the step's own buttons. */}
      <Box className="op-action-bar" data-testid="form-action-bar">
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            size="small"
            sx={{
              ...DANGER_BUTTON_SX,
              height: 38,
              fontSize: "13px",
              minWidth: { xs: 72, sm: 96 },
            }}
            onClick={() => setConfirmCancel(true)}
            data-testid="form-cancel"
          >
            Cancel
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button
            variant="outlined"
            size="small"
            sx={{
              ...SECONDARY_BUTTON_SX,
              height: 38,
              fontSize: "13px",
              minWidth: { xs: 72, sm: 96 },
              "&.Mui-disabled": { borderColor: "#EAECF0", color: "#98A2B3", backgroundColor: "#fff" },
            }}
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Back
          </Button>

          {activeStep === 2 ? (
            <Button
              variant="contained"
              size="small"
              sx={{
                ...PRIMARY_BUTTON_SX,
                minHeight: 38,
                fontSize: "13px",
                minWidth: { xs: 104, sm: 140 },
                // the long Update wording wraps inside the button instead of pushing the bar wide (on a phone it takes
                // the bar's second line, full width)
                ...(updateLabel && { whiteSpace: "normal", lineHeight: 1.25, maxWidth: { xs: "100%", sm: 340 }, flex: { xs: "1 1 100%", sm: "0 1 auto" } }),
              }}
              onClick={() => saveCustomerDetails(values)}
            >
              {isEditMode ? updateLabel || "Update" : "Create"}
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              sx={{
                ...PRIMARY_BUTTON_SX,
                height: 38,
                fontSize: "13px",
                minWidth: { xs: 104, sm: 140 },
              }}
              onClick={handleNextClick}
            >
              {activeStep === 0 ? (showCalculationResult ? "Next" : "Calculate") : "Next"}
            </Button>
          )}
        </Box>
      </Box>

      <ConfirmBox
        open={confirmCancel}
        // ConfirmBox shows `content` as its question; `title` only picks the (red) look
        title="Cancel"
        content={isEditMode ? "Cancel this edit? Your changes will not be saved." : "Cancel this policy? Everything entered will be lost."}
        subContent="You will go back to the Online Policy list."
        confirmButton="Yes, cancel"
        cancelButton="Stay on the form"
        setOpenConfirmBox={setConfirmCancel}
        Function={() => {
          setConfirmCancel(false);
          handleReset();
        }}
      />
    </>
  );
};

export default FormNavigationButtons;
